import { getViewportGraphSlotFor } from '../editor/editorSession.js';
import { toRenderSlot, type AppState, type ViewportMode, type ViewportTarget } from '../state/appState.js';
import { getValidationStateFor } from '../editor/validationState.js';
import { resetWebGPUState, WebGPUDiagnosticError } from '../rendering/webgpuRenderer.js';
import { resetCanvasSceneRendererCache } from '../rendering/canvasSceneRenderer.js';
import { getActiveSdfTheme } from './activeSdfTheme.js';
import { errorMessageOf } from '../ui/errors.js';
import { type LayoutMap, type LayoutResult } from '../scene/layoutEngine.js';
import type { ViewportCanvasController } from './viewportCanvas.js';
import type { ViewportOverlayController } from './viewportOverlay.js';
import { renderSceneViewportFor, renderStandaloneViewportFor } from './viewportRenderer.js';
import type { RenderStatusPresenter } from './renderStatusPresenter.js';
import type { Scene, SceneNodeId } from '../scene/sceneState.js';
import { getEffectiveViewportResolution, type ViewportResolution } from './viewportResolution.js';
import { syncViewportRulers } from './viewportRulers.js';
import { resolveViewportRenderTarget } from './viewportRenderTarget.js';
import type { DataRuntimeManager } from './dataRuntime.js';

interface RenderLoopDeps {
  appState: AppState;
  viewportCanvas: ViewportCanvasController;
  overlay: ViewportOverlayController;
  statusPresenter: RenderStatusPresenter;
  syncEditor: () => boolean;
  notifyEditorTargetSynced: () => void;
  getViewportFocusTarget?: () => ViewportTarget | null;
  getSelectedSceneNodeId?: () => SceneNodeId | null;
  dataRuntime?: DataRuntimeManager;
}

export interface ViewportPickContext {
  scene: Scene;
  layoutMap: LayoutMap;
  resolution: ViewportResolution;
  ownerComponentId: string | null;
  pickTarget: ViewportTarget | null;
  mode: ViewportMode;
}

export interface RenderLoopController {
  scheduleRender(): void;
  forceRender(): void;
  renderNow(): Promise<void>;
  resetRenderState(): void;
  getLastSceneLayout(): LayoutResult | null;
  /** Scene/layout/resolution of the last scene render, for viewport picking. */
  getPickContext(): ViewportPickContext | null;
}

export function createRenderLoopController(deps: RenderLoopDeps): RenderLoopController {
  let renderPending = false;
  let renderInFlight = false;
  let renderFrameRequested = false;
  let lastLayout: LayoutResult | null = null;
  let lastScene: Scene | null = null;
  let lastResolution: ViewportResolution | null = null;
  let lastPickOwnerComponentId: string | null = null;
  let lastPickTarget: ViewportTarget | null = null;
  let lastPickMode: ViewportMode = 'scene';

  function requestRenderFrame(): void {
    if (renderFrameRequested) return;
    renderFrameRequested = true;
    requestAnimationFrame(() => {
      renderFrameRequested = false;
      void doRender();
    });
  }

  function scheduleRender(): void {
    if (!renderPending) {
      renderPending = true;
      if (!renderInFlight) requestRenderFrame();
    }
  }

  function forceRender(): void {
    renderPending = true;
    if (!renderInFlight) requestRenderFrame();
  }

  async function renderNow(): Promise<void> {
    renderPending = true;
    if (!renderInFlight) {
      await doRender();
      return;
    }
    await new Promise<void>(resolve => {
      const waitForRender = (): void => {
        if (renderInFlight) {
          requestAnimationFrame(waitForRender);
          return;
        }
        void doRender().then(resolve);
      };
      requestAnimationFrame(waitForRender);
    });
  }

  function showEmptyViewport(resolution: ViewportResolution, clearColor: readonly [number, number, number], status: string): void {
    // Draws past the persistent ScenePainter — its framebuffer/snapshots no
    // longer match the canvas, so the next scene render must repaint fully.
    resetCanvasSceneRendererCache();
    const canvas = deps.viewportCanvas.getCanvas('2d');
    canvas.width = resolution.w;
    canvas.height = resolution.h;
    syncViewportRulers(resolution);
    const ctx2d = canvas.getContext('2d');
    if (ctx2d) {
      ctx2d.fillStyle = `rgb(${Math.round(clearColor[0] * 255)} ${Math.round(clearColor[1] * 255)} ${Math.round(clearColor[2] * 255)})`;
      ctx2d.fillRect(0, 0, resolution.w, resolution.h);
    }
    deps.overlay.clear();
    lastScene = null;
    lastResolution = null;
    lastPickOwnerComponentId = null;
    lastPickTarget = null;
    lastPickMode = deps.appState.viewportMode;
    deps.viewportCanvas.present();
    deps.statusPresenter.showEmptyViewport(status);
  }

  function hasStandaloneOutput(target: ReturnType<typeof resolveViewportRenderTarget>): boolean {
    if (target.kind !== 'standalone') return false;
    return target.standaloneGraphs.geometry.nodes.some(node => node.type === 'Output')
      && target.standaloneGraphs.material.nodes.some(node => node.type === 'Output');
  }

  async function doRender(): Promise<void> {
    if (renderInFlight) return;
    renderInFlight = true;
    renderPending = false;
    const appState = deps.appState;
    try {
      // Sync editor edits back to the target so scene rendering sees them.
      const synced = deps.syncEditor();
      if (synced) {
        deps.notifyEditorTargetSynced();
      }

      const res = getEffectiveViewportResolution(appState);
      const backend = appState.backend;
      const contextType = backend === 'canvas' ? '2d' : 'webgpu';
      const validation = getValidationStateFor(appState);
      const sdfTheme = getActiveSdfTheme();
      const firstWarning = validation.issues.find(issue => issue.level === 'warning');

      const renderTarget = resolveViewportRenderTarget(appState, deps.getViewportFocusTarget?.() ?? null);
      const viewportMode = appState.viewportMode;
      const renderSlot = toRenderSlot(getViewportGraphSlotFor(appState));
      deps.statusPresenter.updateRenderMode(appState, renderSlot);

      if (renderTarget.kind === 'empty') {
        showEmptyViewport(
          res,
          sdfTheme.clearColor,
          `${renderTarget.statusLabel} • ${renderTarget.message}`,
        );
        return;
      }

      if (renderTarget.kind === 'scene' && renderTarget.scene.rootIds.length === 0) {
        showEmptyViewport(res, sdfTheme.clearColor, `${renderTarget.statusLabel} • Scene is empty`);
        return;
      }

      if (renderTarget.kind === 'standalone' && !hasStandaloneOutput(renderTarget)) {
        showEmptyViewport(
          res,
          sdfTheme.clearColor,
          `${renderTarget.statusLabel} • Add an Output node to render the graph.`,
        );
        return;
      }

      const canvas = deps.viewportCanvas.getCanvas(contextType);
      syncViewportRulers(res);

      const t0 = performance.now();
      if (renderTarget.kind === 'scene') {
        lastLayout = await renderSceneViewportFor({
          appState,
          canvas,
          resolution: res,
          scene: renderTarget.scene,
          sceneContextOptions: renderTarget.sceneContextOptions,
          renderSlot,
          sdfTheme,
          dataRuntime: appState.runtimePreview ? deps.dataRuntime : undefined,
        });
        deps.overlay.render(res, renderTarget.scene, lastLayout.layoutMap, {
          selectedId: deps.getSelectedSceneNodeId?.() ?? null,
        });
        lastScene = renderTarget.scene;
        lastResolution = res;
        lastPickOwnerComponentId = renderTarget.ownerComponentId;
        lastPickTarget = renderTarget.pickTarget;
        lastPickMode = viewportMode;
      } else {
        const result = await renderStandaloneViewportFor({
          appState,
          canvas,
          resolution: res,
          standaloneGraphs: renderTarget.standaloneGraphs,
          renderSlot,
          sdfTheme,
          dataRuntime: appState.runtimePreview ? deps.dataRuntime : undefined,
        });
        if (!result.rendered) return;
        if (result.layoutResult) lastLayout = result.layoutResult;
        deps.overlay.clear();
        lastScene = null;
        lastResolution = null;
        lastPickOwnerComponentId = null;
        lastPickTarget = null;
        lastPickMode = viewportMode;
      }
      deps.statusPresenter.showRenderSuccess({
        backend,
        sourceLabel: renderTarget.statusLabel,
        sizeMode: appState.viewportSizeMode,
        resolution: res,
        elapsedMs: performance.now() - t0,
        warning: firstWarning?.message,
      });
      deps.viewportCanvas.present();
    } catch (err) {
      const backend = appState.backend;
      if (backend === 'webgpu') {
        resetWebGPUState();
        deps.statusPresenter.showRenderError({
          status: 'WebGPU error — check console',
          error: {
            title: 'WebGPU Render Error',
            message: errorMessageOf(err),
            source: err instanceof WebGPUDiagnosticError ? `webgpu:${err.stage}` : 'rendering/webgpuRenderer',
            details: err instanceof WebGPUDiagnosticError ? err.details : undefined,
          },
        });
        console.error(err);
      } else {
        deps.statusPresenter.showRenderError({
          status: 'Error: ' + (err as Error).message,
          error: {
            title: 'Canvas Render Error',
            message: errorMessageOf(err),
            source: 'rendering/canvasRenderer',
          },
        });
        console.error(err);
      }
    } finally {
      renderInFlight = false;
      if (renderPending) requestRenderFrame();
    }
  }

  return {
    scheduleRender,
    forceRender,
    renderNow,
    resetRenderState(): void {
      resetWebGPUState();
      resetCanvasSceneRendererCache();
      deps.dataRuntime?.reset();
    },
    getLastSceneLayout(): LayoutResult | null {
      return lastLayout;
    },
    getPickContext(): ViewportPickContext | null {
      if (!lastScene || !lastLayout || !lastResolution) return null;
      return {
        scene: lastScene,
        layoutMap: lastLayout.layoutMap,
        resolution: lastResolution,
        ownerComponentId: lastPickOwnerComponentId,
        pickTarget: lastPickTarget,
        mode: lastPickMode,
      };
    },
  };
}
